import { useCallback } from 'react';
import { useSetAtom } from 'jotai';

import { useRouteInfo } from '@/hooks/use-route-info';
import { useItemActions } from '@/hooks/use-item-actions';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { useBlockDuplicate, useNoteDuplicate, useUserSourceDuplicate } from '@/hooks/rxdb-models';

import { appRoutes } from '@/constants/routes';
import { itemTypes } from '@/constants/item-types';
import { toggleObject } from '@/utils/helper-utils';
import { multiSelectionsAtom, sidebarOverlayAtom } from '@/state';
import { conditionalSpread } from '@flintmint/web-shared/utils/helper-utils';

import type { RxDocument } from 'rxdb';
import type { DocumentWithType } from '@/constants/types';
import type { MenuProps } from '@flintmint/web-shared/components/menu';
import type { SearchableDocument } from '@/services/search-service/orama-schema';
import type { Block, Note, UserSource } from '@flintmint/core-shared/rxdb';
import type { MenuItemProps } from '@flintmint/web-shared/components/menu-item';

export type MenuOptionKey =
  | 'open'
  | 'openInNewTab'
  | 'duplicate'
  | 'select'
  | 'setShortcut'
  | 'manageTags'
  | 'delete'
  | 'removeFromTag'
  | 'preview';

const baseOptions: MenuOptionKey[] = ['open', 'openInNewTab', 'duplicate', 'select', 'setShortcut', 'manageTags', 'delete'];

type MenuOverride = Partial<MenuItemProps> | ((item: MenuItemProps, data: DocumentWithType) => MenuItemProps);

type MenuOverrides = Partial<Record<MenuOptionKey, MenuOverride>>;

export const useDocuMenu = (options: MenuOptionKey[] = baseOptions, overrides?: MenuOverrides) => {
  const setMultiSelections = useSetAtom(multiSelectionsAtom);
  const setSidebarOverlay = useSetAtom(sidebarOverlayAtom);

  const navigateWithSearch = useNavigateWithSearch();
  const { basePath } = useRouteInfo();

  const duplicateNoteMutation = useNoteDuplicate();
  const duplicateBlockMutation = useBlockDuplicate();
  const duplicateUserSourceMutation = useUserSourceDuplicate();

  const { openDocument, configureDestructiveOptions, configureRemoveFromTagOption } = useItemActions();

  const openPreview = (data: DocumentWithType) => {
    setSidebarOverlay({
      type: 'document-preview',
      context: {
        type: data.type,
        docId: data.id,
      },
    });
  };

  const onDuplicate = (data: DocumentWithType, onSuccess?: (id: string) => void) => {
    switch (data.type) {
      case 'notes':
      case 'templates':
        return duplicateNoteMutation.mutate(data.id, {
          onSuccess: (duplicatedData: RxDocument<Note>) => {
            if (basePath === appRoutes.library) {
              navigateWithSearch(`${appRoutes.library}/${duplicatedData.id}`);
            } else {
              openPreview({ ...duplicatedData.toJSON(), type: data.type });
            }
          },
        });
      case 'blocks':
        return duplicateBlockMutation.mutate(data.id, {
          onSuccess: (duplicatedData: RxDocument<Block>) => {
            if (onSuccess) {
              onSuccess(duplicatedData.id);
              return;
            }

            if (basePath !== appRoutes.library) {
              openPreview({ ...duplicatedData.toJSON(), type: data.type });
            }
          },
        });
      case 'userSources':
        return duplicateUserSourceMutation.mutate(data.id, {
          onSuccess: (duplicatedData: RxDocument<UserSource>) => {
            if (basePath === appRoutes.cortex) {
              navigateWithSearch(`${appRoutes.cortex}/${duplicatedData.id}`);
            } else {
              openPreview({ ...duplicatedData.toJSON(), type: data.type });
            }
          },
        });
    }
    return null;
  };

  return useCallback(
    (data: DocumentWithType): MenuProps['data'] => {
      const menuMap: Record<MenuOptionKey, MenuItemProps[]> = {
        open: [
          {
            icon: 'link-square-02',
            name: `Open ${itemTypes[data.type]?.name}`,
            onClick: () => openDocument(data),
          },
        ],
        ...conditionalSpread(
          {
            openInNewTab: [
              {
                icon: 'link-square-02',
                name: `Open ${itemTypes[data.type]?.name} in new tab`,
                onClick: () => openDocument(data, { openInNewTab: true }),
              },
            ],
          },
          data.type !== 'blocks'
        ),
        preview: [
          {
            icon: 'view',
            name: 'Preview',
            onClick: () => openPreview(data),
          },
        ],
        duplicate: [
          { type: 'divider' },
          {
            icon: 'copy-01',
            name: 'Clone',
            'data-testid': 'clone-btn',
            onClick: () => onDuplicate(data),
          },
          ...conditionalSpread(
            [
              {
                icon: 'copy-edit',
                name: `Clone and edit`,
                'data-testid': 'clone-and-edit-btn',
                onClick: () =>
                  onDuplicate(data, (blockId) => {
                    setSidebarOverlay({
                      blockId,
                      type: 'blocks',
                      context: {
                        skipOriginalBlockWarning: true,
                      },
                    });
                  }),
              },
            ],
            data.type === 'blocks' && basePath === appRoutes.library
          ),
          { type: 'divider' },
        ],
        select: [
          {
            shortcut: 'CommandOrControl+Click',
            icon: 'checkmark-square-02',
            name: 'Select',
            onClick: () => {
              setMultiSelections((arr) => toggleObject(arr || [], data));
            },
          },
          { type: 'divider' },
        ],
        setShortcut: [
          {
            disabled: true,
            name: 'Set Shortcut',
            icon: 'command-stroke',
            shortcut: 'CommandOrControl+Shift+X',
            onClick: () => {},
          },
        ],
        manageTags: [
          {
            icon: 'tags',
            disabled: true,
            name: 'Manage Tags',
            shortcut: 'CommandOrControl+T',
            onClick: () => {},
          },
          { type: 'divider' },
        ],
        removeFromTag: [{ ...configureRemoveFromTagOption([data as SearchableDocument]) }],
        delete: configureDestructiveOptions([data]),
      };

      const selectedOptions = options && options.length > 0 ? options : (Object.keys(menuMap) as MenuOptionKey[]);

      const applyOverride = (key: MenuOptionKey, items: MenuItemProps[]): MenuItemProps[] => {
        const override = overrides?.[key];
        if (!override) return items;

        return items.map((item) => {
          if (item.type === 'divider') return item;

          if (typeof override === 'function') {
            return override(item, data);
          }

          return { ...item, ...override };
        });
      };

      return selectedOptions.flatMap((optionKey) => applyOverride(optionKey, menuMap[optionKey] ?? []));
    },
    [duplicateNoteMutation, options]
  );
};
